import React, { useEffect, useMemo, useRef, useState } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import { AccentTextUnbound } from '../unbound/AccentTextUnBound';

// Generates default initials from a full name. User can override in the UI.
const generateInitials = (name) => {
    if (!name) return "";

    // Strip trailing "(...)" contact details if present
    const cleaned = String(name).replace(/\s*\([^()]*\)\s*$/, '').trim();
    if (!cleaned) return "";

    const parts = cleaned
        .split(/\s+/)
        .map(p => p.trim())
        .filter(Boolean);

    // Take first letter of up to 3 words (e.g., "John Q Public" -> "JQP")
    return parts
        .slice(0, 3)
        .map(p => (p[0] ? p[0].toUpperCase() : ""))
        .join("");
};

export const SignaturePad = ({ onSignatureSave, readOnly, onCancel, signer }) => {
    const sigCanvas = useRef({});

    const [showAccept, setShowAccept] = React.useState(false);

    // Show signer field only if the prop was actually provided
    const showSigner = typeof signer !== 'undefined';

    // AccentTextUnbound is "unbound" (uses defaultValue once), so keep local state and key it for refresh on prop changes
    const [signerName, setSignerName] = useState(signer ?? "");
    useEffect(() => setSignerName(signer ?? ""), [signer]);
    const signerKey = useMemo(() => `signer_${signer ?? ""}`, [signer]);

    // Initials: default from signer, but user can edit
    const [initials, setInitials] = useState(generateInitials(signer));
    useEffect(() => setInitials(generateInitials(signer)), [signer]);
    const initialsKey = useMemo(() => `initials_${signer ?? ""}`, [signer]);

    const clear = () => {
        sigCanvas.current.clear();
        setShowAccept(false);
    };

    const save = () => {

        const isEmpty = sigCanvas.current.isEmpty();

        if (isEmpty) {
            onSignatureSave(
                null,
                showSigner ? signerName : undefined,
                showSigner ? initials : undefined
            );
            return;
        }

        const dataUrl = sigCanvas.current.toDataURL('image/png');

        // 2nd arg is backwards compatible: existing handlers ignore it
        onSignatureSave(
            dataUrl,
            showSigner ? signerName : undefined,
            showSigner ? initials : undefined
        );
    };


    const style = readOnly ? { pointerEvents: 'none' } : undefined;

    const btnStyle = { minWidth: "80px" };

    return (
        <div style={style}>
            {showSigner && (
                <div style={{ width: 500, marginBottom: 8 }}>
                    <div className="d-flex" style={{ gap: 8 }}>
                        <div style={{ flex: 1 }}>
                            <AccentTextUnbound
                                key={signerKey}
                                defaultValue={signerName}
                                label="Signer name"
                                disabled={readOnly}
                                onChange={v => setSignerName(v ?? "")}
                            />
                        </div>
                        <div style={{ width: 120 }}>
                            <AccentTextUnbound
                                key={initialsKey}
                                defaultValue={initials}
                                label="Initials"
                                disabled={readOnly}
                                maxLength={6}
                                onChange={v => setInitials((v ?? "").toUpperCase())}
                            />
                        </div>
                    </div>
                </div>
            )}
            <SignatureCanvas
                ref={sigCanvas}
                penColor="black"
                canvasProps={{
                    width: 500,
                    height: 200,
                    className: 'signature-pad',
                    style: {
                        border: '1px solid #d9d9d9',
                        borderRadius: '2px'
                    }
                }}
                onBegin={e => setShowAccept(true) }
            />
            {!readOnly && <div className="d-flex justify-content-between">
                <div><button style={ btnStyle} onClick={onCancel}>Cancel</button></div>
                <div><button style={btnStyle} onClick={clear}>Clear</button></div>
                <div>{<button style={btnStyle} onClick={save} disabled={!showAccept}>Accept</button>}</div>
            </div>}
        </div>
    );
};


